/**
 * CA Section Manager
 * 主入口：管理和注册所有 CA Section Web Components
 * 
 * 架构说明：
 * - 每个 section 是一个独立的 Web Component
 * - 使用 ES modules (type=module) 动态加载
 * - 支持降级方案：如果不支持动态 import，使用 script 标签加载
 * - 统一的组件注册和管理机制
 * - 首屏组件（如 ca-header）直接导入，不等待动态加载
 */

    
// 已知的所有组件名称列表（与 components/index.js 保持同步）
// 直接查询这些组件比遍历整个 DOM 快得多
const knownComponents = [
  'ca-header', 'ca-footer', 'ca-faq',
  'ca-hero-banner', 'ca-testimonials', 'ca-newsletter',
  'ca-product-grid', 'ca-challenge-solution', 'ca-why-choose',
  'ca-related-solutions', 'ca-installation-checklist'
];

class CASectionManager {
  constructor() {
    this.components = new Map();
    this.loadedComponents = new Set();
    this.loadingComponents = new Set();
    this.observer = null;
    // 从当前脚本路径推断 basePath
    this.basePath = this.getBasePath();
    this.supportsDynamicImport = this.checkDynamicImportSupport();
  }

  /**
   * 获取组件的基础路径
   */
  getBasePath() {
    // 尝试从全局变量获取（如果 Shopify 设置了）
    if (typeof window !== 'undefined' && window.CA_COMPONENTS_BASE_PATH) {
      return window.CA_COMPONENTS_BASE_PATH;
    }
    
    // 通过 data-comp="ca-section-manager" 属性查找 script 标签
    const script = document.querySelector('script[data-comp="ca-section-manager"]');
    if (script && script.src) {
      const scriptPath = script.src;
      const baseUrl = scriptPath.substring(0, scriptPath.lastIndexOf('/') + 1);
      return baseUrl + '';
    }
    
    // 降级到默认路径
    return '/assets/';
  }

  /**
   * 检测浏览器是否支持动态 import
   */
  checkDynamicImportSupport() {
    try {
      // 检查是否支持动态 import
      // 在模块环境中，import 是一个函数
      // 使用 eval 来安全地检查，避免在非模块环境中报错
      if (typeof window !== 'undefined') {
        // 在浏览器环境中，检查是否在模块上下文中
        const script = document.currentScript;
        if (script && script.type === 'module') {
          return true;
        }
        // 尝试检查是否支持
        return 'noModule' in HTMLScriptElement.prototype;
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  /**
   * 使用动态 import 加载组件
   */
  async loadComponentWithImport(componentName) {
    try {
      const module = await import(`${this.basePath}${componentName}.js`);
      return module.default || module[this.toPascalCase(componentName)];
    } catch (error) {
      console.error(`Failed to load ${componentName} with dynamic import:`, error);
      throw error;
    }
  }

  /**
   * 使用 script 标签降级加载组件
   */
  loadComponentWithScript(componentName) {
    return new Promise((resolve, reject) => {
      // 检查是否已经定义
      if (customElements.get(componentName)) {
        // 如果已经定义，尝试从已加载的模块中获取
        const existingComponent = this.components.get(componentName);
        if (existingComponent) {
          resolve(existingComponent);
          return;
        }
      }

      const script = document.createElement('script');
      script.type = 'module';
      script.src = `${this.basePath}${componentName}.js`;
      
      // 使用事件监听器来检测组件加载
      let resolved = false;
      
      // 监听自定义事件（组件加载完成后会触发）
      const loadHandler = (event) => {
        if (event.detail.componentName === componentName) {
          resolved = true;
          clearInterval(checkInterval);
          window.removeEventListener('component-loaded', loadHandler);
          const ComponentClass = customElements.get(componentName);
          if (ComponentClass) {
            resolve(ComponentClass);
          } else {
            reject(new Error(`Component ${componentName} loaded but not registered`));
          }
        }
      };
      
      window.addEventListener('component-loaded', loadHandler);
      
      // 轮询检查组件是否已注册
      const checkInterval = setInterval(() => {
        if (customElements.get(componentName)) {
          if (!resolved) {
            resolved = true;
            clearInterval(checkInterval);
            script.removeEventListener('component-loaded', loadHandler);
            resolve(customElements.get(componentName));
          }
        }
      }, 50);

      // 超时处理
      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          clearInterval(checkInterval);
          window.removeEventListener('component-loaded', loadHandler);
          if (customElements.get(componentName)) {
            resolve(customElements.get(componentName));
          } else {
            reject(new Error(`Timeout loading ${componentName}`));
          }
        }
      }, 5000);

      script.onerror = () => {
        if (!resolved) {
          resolved = true;
          clearInterval(checkInterval);
          clearTimeout(timeoutId);
          window.removeEventListener('component-loaded', loadHandler);
          reject(new Error(`Failed to load script for ${componentName}`));
        }
      };

      script.onload = () => {
        // 脚本加载完成，等待组件注册
        // 如果 100ms 后还没注册，可能是组件代码有问题
        setTimeout(() => {
          if (!resolved && !customElements.get(componentName)) {
            console.warn(`Script loaded for ${componentName} but component not registered yet`);
          }
        }, 100);
      };

      document.head.appendChild(script);
    });
  }

  /**
   * 动态加载组件模块
   */
  async loadComponent(componentName) {
    // 如果已经加载，直接返回
    if (this.loadedComponents.has(componentName)) {
      return this.components.get(componentName);
    }

    // 如果正在加载，等待加载完成
    if (this.loadingComponents.has(componentName)) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.loadedComponents.has(componentName)) {
            clearInterval(checkInterval);
            resolve(this.components.get(componentName));
          }
        }, 50);
      });
    }

    this.loadingComponents.add(componentName);

    try {
      let ComponentClass;

      // 优先使用动态 import
      if (this.supportsDynamicImport) {
        try {
          ComponentClass = await this.loadComponentWithImport(componentName);
        } catch (importError) {
          console.warn(`Dynamic import failed for ${componentName}, falling back to script tag:`, importError);
          // 降级到 script 标签
          ComponentClass = await this.loadComponentWithScript(componentName);
        }
      } else {
        // 直接使用 script 标签降级
        ComponentClass = await this.loadComponentWithScript(componentName);
      }

      // 注册组件
      this.register(componentName, ComponentClass);
      return ComponentClass;
    } catch (error) {
      console.error(`Failed to load component ${componentName}:`, error);
      this.loadingComponents.delete(componentName);
      throw error;
    }
  }

  /**
   * 注册一个组件
   */
  register(name, componentClass) {
    if (this.components.has(name)) {
      console.warn(`Component ${name} is already registered`);
      return;
    }

    // 检查是否已经定义
    if (customElements.get(name)) {
      console.warn(`Custom element ${name} is already defined`);
      return;
    }

    try {
      customElements.define(name, componentClass);
      this.components.set(name, componentClass);
      this.loadedComponents.add(name);
      this.loadingComponents.delete(name);
    } catch (error) {
      console.error(`Failed to register component ${name}:`, error);
      this.loadingComponents.delete(name);
    }
  }

  /**
   * 发现页面中使用的组件标签
   * 使用高效的查询方式：直接查询已知组件，避免遍历整个 DOM
   */
  discoverComponents() {
    const componentNames = new Set();
    
    // 使用 querySelectorAll 批量查询已知组件
    // 浏览器会优化这些查询，比遍历 DOM 快得多
    knownComponents.forEach(componentName => {
      // querySelectorAll 使用浏览器原生优化，非常快
      if (document.querySelector(componentName)) {
        componentNames.add(componentName);
      }
    });
    
    // 也查找可能还未注册的元素（通过 data-component 属性）
    // 属性选择器查询也很快
    const elementsWithData = document.querySelectorAll('[data-component^="ca-"]');
    elementsWithData.forEach(el => {
      if (el.dataset?.component) {
        componentNames.add(el.dataset.component);
      }
    });

    return Array.from(componentNames);
  }

  /**
   * 自动发现并加载页面中的组件
   */
  async autoLoad() {
    const componentNames = this.discoverComponents();
    
    // 过滤掉已经注册的组件（如 ca-header 在主入口已导入）
    const componentsToLoad = componentNames.filter(name => !customElements.get(name));

    if (componentsToLoad.length === 0) return;

    // 并行加载所有发现的组件
    const loadPromises = componentsToLoad.map(name =>
      this.loadComponent(name).catch(err => {
        console.warn(`Failed to auto-load ${name}:`, err);
      })
    );

    await Promise.all(loadPromises);
  }

  /**
   * 使用 MutationObserver 监听 DOM 变化，自动加载新组件
   */
  observe() {
    if (this.observer) {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      const newComponents = new Set();
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // 检查是否是自定义元素
            if (node.tagName && node.tagName.toLowerCase().startsWith('ca-')) {
              const componentName = node.tagName.toLowerCase();
              if (!this.loadedComponents.has(componentName) && 
                  !this.loadingComponents.has(componentName)) {
                newComponents.add(componentName);
              }
            }

            // 检查 data-component 属性
            if (node.dataset?.component && node.dataset.component.startsWith('ca-')) {
              const componentName = node.dataset.component;
              if (!this.loadedComponents.has(componentName) && 
                  !this.loadingComponents.has(componentName)) {
                newComponents.add(componentName);
              }
            }

            // 检查子元素（查找所有 ca- 开头的元素）
            if (node.querySelectorAll) {
              const children = node.querySelectorAll('*');
              children.forEach(child => {
                const tagName = child.tagName?.toLowerCase();
                const componentName = child.dataset?.component || tagName;
                if (componentName && componentName.startsWith('ca-') &&
                    !this.loadedComponents.has(componentName) && 
                    !this.loadingComponents.has(componentName)) {
                  newComponents.add(componentName);
                }
              });
            }
          }
        });
      });

      // 加载新发现的组件
      newComponents.forEach(name => {
        this.loadComponent(name).catch(console.warn);
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * 工具方法：转换为 PascalCase
   */
  toPascalCase(str) {
    return str
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  /**
   * 初始化管理器
   */
  async init() {
    // 自动发现并加载其他组件
    await this.autoLoad();

    // 开始监听 DOM 变化
    this.observe();
  }
}

// 创建全局管理器实例
const caSectionManager = new CASectionManager();

// DOM 加载完成后自动初始化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => caSectionManager.init());
} else {
  caSectionManager.init();
}

// 导出管理器（用于手动控制）
export default caSectionManager;
